/**
 * Capsule recovery — the copy of the capsule this device keeps while it is being
 * written.
 *
 * A creator builds a capsule over an evening on a phone. Until now the Capsule
 * studio had no autosave of any kind: leaving it with work in progress asked
 * "discard this capsule?" and, on the way out, threw every slide away. That is the
 * one gone-wrong a creator cannot undo, and the warning did not prevent it — it
 * only announced it.
 *
 * So the studio keeps a snapshot of the capsule as it is edited, and offers it back
 * the next time it opens. Three decisions shape how, and they are deliberately the
 * same three the FlashBook studio made, because a creator should not have to learn
 * two different sets of promises:
 *
 *  - **On this device, not on the server.** A snapshot is device storage, not a
 *    record. Saving a *draft* is what makes work real and follows the creator
 *    between devices; this only keeps a copy while they are writing. An autosave
 *    that quietly wrote draft records would fill the drafts list with capsules
 *    nobody chose to save.
 *  - **Replaced, not appended.** One slot, because there is one capsule in
 *    progress. A history of snapshots would need a browser to choose from, and the
 *    creator asked for their work back, not for version control.
 *  - **Offered, never applied.** Loading a snapshot over a capsule the studio
 *    already put on screen would be the app deciding which of two pieces of work is
 *    real. It is offered, with the time it was taken, and the creator says yes.
 *
 * The snapshot holds text and structure, not files. Every slide's own content comes
 * back exactly, but a picked *cover film* is a local file the server has never
 * seen — a draft does not carry it either, because it is uploaded when the capsule
 * is published — so a capsule restored on this device comes back without its film.
 * That limit is stated where the creator meets it rather than discovered later.
 */
import {device} from '#/storage'
import {type Slide} from '#/types/capsule'

/**
 * The capsule, in the shape a save would carry.
 *
 * Deliberately the *record's* shape rather than the studio's screen state: a
 * snapshot is not the screen, and writing the studio's field names into storage
 * would make every rename in the studio a migration. That means the cover slide is
 * not in `slides` — the record holds the title and description as their own fields,
 * and the studio rebuilds the cover from them — which is exactly how publishing
 * already divides the capsule up.
 */
export type CapsuleRecoveredDraft = {
  title: string
  description: string
  themeId: string
  /** The music, in the record's spelling rather than the screen's. */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  /** The content slides, i.e. everything after the cover. */
  slides: Slide[]
}

export type CapsuleSnapshot = {
  /**
   * The draft record this work came from, or `null` for a capsule started here.
   *
   * This is what makes the offer trustworthy. Without it, a snapshot left behind by
   * one capsule would be offered over a different one — a creator opening an old
   * draft would be asked whether they want to restore the capsule they abandoned
   * last week, and the honest answer would be "no, and why are you asking".
   */
  draftUri: string | null
  /** When the snapshot was taken, so the offer can say how old it is. */
  savedAt: string
  /**
   * The capsule's own content fingerprint at the moment it was taken.
   *
   * Carried so the offer can answer the only question that matters when the studio
   * opens: *is this different from the capsule already on screen?* A snapshot that
   * matches what is loaded is not unfinished work, it is work that has been dealt
   * with.
   */
  signature: string
  capsule: CapsuleRecoveredDraft
}

/**
 * The capsule a snapshot holds, as the studio hands it over.
 *
 * `signature` is passed in rather than recomputed, so there is exactly one
 * definition of "the content changed" in the codebase — the studio's
 * `capsuleContentSignature` — and the snapshot can never disagree with the
 * unsaved-work guard about whether the same two capsules are the same capsule.
 */
export function createCapsuleSnapshot({
  draftUri,
  signature,
  capsule,
  now = new Date().toISOString(),
}: {
  draftUri: string | null
  signature: string
  capsule: CapsuleRecoveredDraft
  now?: string
}): CapsuleSnapshot {
  return {draftUri, signature, savedAt: now, capsule}
}

/**
 * Whether a capsule is worth keeping a copy of.
 *
 * The same question the unsaved-work guard asks, and deliberately the same answer:
 * a capsule with a title, a description, a theme of its own or a single written
 * slide. An empty studio is not work, and offering to recover one would teach the
 * creator to dismiss the offer without reading it — which is how a recovery feature
 * becomes a dialog everybody clicks through.
 *
 * The cover's *film* is deliberately not counted. It is not something a copy can
 * bring back, so a capsule whose only content is an attached file is genuinely
 * empty as far as this question is concerned.
 */
export function capsuleHasContent(capsule: {
  title: string
  description: string
  slides: readonly Slide[]
}): boolean {
  if (capsule.title.trim().length > 0) return true
  if (capsule.description.trim().length > 0) return true
  return capsule.slides.some(
    slide =>
      (slide.contentTitle ?? '').trim().length > 0 ||
      (slide.contentText ?? '').trim().length > 0 ||
      (slide.question ?? '').trim().length > 0,
  )
}

export function getCapsuleSnapshot(): CapsuleSnapshot | null {
  const snapshot = device.get(['capsuleRecovery'])
  return snapshot ?? null
}

export function putCapsuleSnapshot(snapshot: CapsuleSnapshot): void {
  device.set(['capsuleRecovery'], snapshot)
}

export function clearCapsuleSnapshot(): void {
  device.remove(['capsuleRecovery'])
}

/** Whether a snapshot belongs to the capsule the studio is currently holding. */
export function snapshotBelongsTo(
  snapshot: Pick<CapsuleSnapshot, 'draftUri'>,
  draftUri: string | null,
): boolean {
  return snapshot.draftUri === draftUri
}

/**
 * The snapshot worth offering back, or `null` when there is nothing to offer.
 *
 * Three ways a stored snapshot is not an offer, and each is a case a creator would
 * otherwise meet as a confusing dialog:
 *
 *  - it belongs to a *different* capsule;
 *  - it is exactly the capsule already on screen — nothing to recover;
 *  - it holds nothing (a capsule abandoned before a first word was typed).
 */
export function offerableCapsuleSnapshot(
  snapshot: CapsuleSnapshot | null,
  current: {draftUri: string | null; signature: string},
): CapsuleSnapshot | null {
  if (!snapshot) return null
  if (!snapshotBelongsTo(snapshot, current.draftUri)) return null
  if (snapshot.signature === current.signature) return null
  if (!capsuleHasContent(snapshot.capsule)) return null
  return snapshot
}
